///
/// EarthBrowser
///
/// Building blocks for legend bodies: a continuous color scale with
/// ticks, a swatch + label row, a divided section, and the source
/// footnote. Legends compose these so their anatomy reads the same.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { cn } from "@/utils/utils"
import type { ReactNode } from "react"

export interface ColorScaleTick {
	/** Position along the bar, 0..100 */
	at: number
	label: string
}

interface ColorScaleProps {
	/** CSS background for the bar, e.g. a linear-gradient() */
	gradient: string
	ticks: ColorScaleTick[]
	/** Caption above the bar */
	label?: string
}

/** Gradient bar with tick labels; edge ticks hug the ends, the rest are centred */
export function ColorScale({ gradient, ticks, label }: ColorScaleProps) {
	return (
		<div className="space-y-1">
			{label && <div className="text-slate-300">{label}</div>}
			<div className="h-2.5 rounded-sm border border-white/20" style={{ background: gradient }} />
			<div className="relative h-3.5 text-[10px] text-slate-400">
				{ticks.map(tick => (
					<span
						key={`${tick.at}-${tick.label}`}
						className={cn(
							"absolute whitespace-nowrap",
							tick.at <= 0 ? "left-0" : tick.at >= 100 ? "right-0" : "-translate-x-1/2"
						)}
						style={tick.at > 0 && tick.at < 100 ? { left: `${tick.at.toFixed(1)}%` } : undefined}
					>
						{tick.label}
					</span>
				))}
			</div>
		</div>
	)
}

/** Build the gradient and evenly spaced ticks for a list of [value, color] stops */
export function gradientFromStops(stops: Array<[number, string]>): string {
	const min = stops[0][0]
	const max = stops[stops.length - 1][0]
	return `linear-gradient(to right, ${stops
		.map(([value, color]) => `${color} ${(((value - min) / (max - min)) * 100).toFixed(1)}%`)
		.join(", ")})`
}

/** Fixed-width swatch column beside a label, so rows align down the card */
export function SymbolRow({ swatch, children }: { swatch: ReactNode; children: ReactNode }) {
	return (
		<div className="flex items-center gap-2">
			<span className="w-9 flex-shrink-0 flex items-center justify-center">{swatch}</span>
			<span className="text-slate-300">{children}</span>
		</div>
	)
}

/** Rows that belong together, separated from what came before by a rule */
export function LegendSection({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn("space-y-1.5 pt-1.5 border-t border-white/10", className)}>{children}</div>
}

/** Data source and reading tips, last in the card */
export function LegendFootnote({ children }: { children: ReactNode }) {
	return (
		<div className="pt-1.5 border-t border-white/10 text-[11px] leading-snug text-slate-400">
			{children}
		</div>
	)
}
